import moment from "moment-timezone";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { UserContext, OrganisationContext } from "../../layouts/MainLayout";

/**Componenets**/
import { Dropdown } from "antd";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import Pagination from "../../utils/components/common/pagination";
import Icons from "../../utils/components/icons";
import AddSchedules from "./AddSchedules";

/**Services**/
import {
  deleteScheduleTemplate,
  getScheduleTemplates,
} from "../../services/SchedulerService";

import classNames from "classnames";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import { formatDate } from "../../utils/utils";
import ScheduleDetails from "./ScheduleDetails";
import "./Schedules.scss";
import RightSidebar from "../../utils/components/common/rightSidebar/RightSidebar";
import FleetSchedule from "./FleetSchedule";
import {
  fleetStatus,
  getCurrentSiteLocalData,
  updateLocalStorageData,
  initStates,
  schdState,
} from "../../utils/constants/scheduleConstants";
import { backendConfig } from "../../config/BackendConfig";


const SchedulesDashboard = () => {
  const { t } = useTranslation("schedules");

  const { siteData } = useContext(OrganisationContext);
  const { appTheme } = useContext(UserContext);

  const [isSchdInfoModalOpen, setIsSchdInfoModalOpen] = useState(false);
  const [isAddScheduleOpen, setIsAddScheduleOpen] = useState(false);
  const [schdActionType, setSchdActionType] = useState("add");
  const [scheduleList, setSchduleList] = useState();
  const [totalSchdCount, setTotalSchdCount] = useState();
  const [filteredSchdCount, setFilteredSchdCount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFleetLoading, setIsFleetLoading] = useState(false);
  const [currentSelectedRow, setCurrentSelectedRow] = useState();
  const [currentPageListCount, setCurrentPageListCount] = useState();
  const [isSchdFleetOpen, setIsSchdFleetOpen] = useState(false);
  const [currentTemplateData, setCurrentTemplateData] = useState(null);
  const sortingAction = useRef(false);

  const actionItems = [
    {
      label: (
        <p
          className="schdActionItems pointer"
          onClick={() => {
            setIsSchdInfoModalOpen(true);
          }}
        >
          <span title={t("schdActions.schdInfoTooltip")} id="schdInfo">
            {t("schdActions.schdInfo")}
          </span>
        </p>
      ),
      key: "schdInfo",
    },
    {
      label: (
        <p
          className="schdActionItems"
          onClick={() => {
            setIsSchdFleetOpen(true);
            updateLocalStorageData({ isOpenFleetPanel: true }, siteData.id);
          }}
        >
          <span title={t("schdActions.fleetSchTooltip")} id="fleetSchTooltip">
            {t("schdActions.fleetSch")}
          </span>
        </p>
      ),
      key: "fleet",
    },
    {
      label: (
        <p
          className="schdActionItems pointer"
          onClick={() => {
            setSchdActionType("edit");
            setIsAddScheduleOpen(true);
          }}
        >
          <span title={t("schdActions.editTooltip")} id="edit">
            {t("schdActions.edit")}
          </span>
        </p>
      ),
      key: "edit",
    },
    {
      label: (
        <p
          className={"schdActionItems pointer"}
          onClick={() => {
            setConfirmModal((prev) => ({
              ...prev,
              open: true,
              msg: t("deleteSchd"),
              type: "deleteSchd",
            }));
          }}
        >
          <span title={t("schdActions.deleteTooltip")} id="delete">
            {t("schdActions.delete")}
          </span>
        </p>
      ),
      key: "delete",
    },
  ];

  const [columList, setColumList] = useState([
    {
      key: "schdName",
      label: t("dashboardHeadings.schdName"),
      val: "asc",
      clickCount: 1,
      sort: true,
    },
    {
      key: "description",
      label: t("dashboardHeadings.description"),
      val: "",
      clickCount: 0,
      sort: false,
    },
    {
      key: "lastUpdatedOn",
      label: t("dashboardHeadings.lastUpdatedOn"),
      val: "",
      clickCount: 0,
      sort: true,
    },
    {
      key: "action",
      label: t("dashboardHeadings.action"),
      val: "",
      clickCount: 0,
      sort: false,
    },
  ]);

  const sortLabel = {
    asc: t("ascSortTitle"),
    desc: t("descSortTitle"),
    "": t("noSortTitle"),
  };

  /**PaginationVariables Starts**/
  const [pageTotalCount, setPageTotalCount] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  /**PaginationVariables Ends**/

  /**Information and confirm modal states: */
  const [infoModal, setInfoModal] = useState({
    open: false,
    msg: "",
    translated: false,
  });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    msg: "",
    type: "",
    data: {},
  });

  const openAddSchedule = useCallback(() => {
    setCurrentSelectedRow();
    setSchdActionType("add");
    setIsAddScheduleOpen(true);
  }, []);

  /**Search functionality Starts */
  const debounceTime = 2000;
  const timeoutCall = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchString, setSearchString] = useState("");

  const debounceCallBack = useCallback((callBackFunc) => {
    if (timeoutCall.current !== null) {
      clearTimeout(timeoutCall.current);
    }
    timeoutCall.current = setTimeout(() => {
      callBackFunc();
    }, debounceTime);
  }, []);

  const resetSearch = useCallback(() => {
    setIsLoading(true);
    setSearchTerm("");
    setCurrentPage(1);
    setSearchString("");
  }, []);

  const handleSearchChange = useCallback(
    (e) => {
      setSearchTerm(e.target.value);
      debounceCallBack(() => {
        setCurrentPage(1);
        setSearchString(e.target.value);
      });
    },
    [debounceCallBack]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        debounceCallBack(() => {
          setCurrentPage(1);
          setSearchString(event.target.value);
        });
      }
    },
    [debounceCallBack]
  );
  /**Search functionality Ends */

  const emptyScreenMsg = useMemo(() => {
    return {
      title: t("emptyMsgs.title"),
      subTitle: searchString
        ? t("emptyMsgs.noSearchResult")
        : t("emptyMsgs.noScheduleAvailable"),
    };
  }, [t, searchString]);

  const getSchedulesList = useCallback(
    async (currentPageNo) => {
      setIsLoading(true);
      setFilteredSchdCount("");
      const localTimeZone = moment.tz.guess();
      const params = {
        siteId: siteData.id,
        pageNo: currentPageNo,
        size: recordsPerPage,
        sort: [],
        search: "",
        userTimezone: localTimeZone,
      };
      if (columList[0].val) {
        params.sort = [
          ...params.sort,
          { scheduleTemplateName: columList[0].val },
        ];
      }
      if (columList[2].val) {
        params.sort = [...params.sort, { updatedDateTime: columList[2].val }];
      }
      if (searchString?.length > 0) {
        params.search = searchString;
      }
      try {
        const response = await getScheduleTemplates(params);
        setSchduleList(response?.data?.Items || []);
        setTotalSchdCount(response?.data?.OverallSchedulesCount);
        searchString &&
          setFilteredSchdCount(response?.data?.TotalSchedulesCount + "/");
        setPageTotalCount(
          Math.ceil(response?.data?.TotalSchedulesCount / recordsPerPage)
        );
        setCurrentPageListCount(response?.data?.QueryCount || 1);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);

        // setSchduleList([]);
        if (error?.response?.StatusCode === "SERVER_ERROR") {
          setInfoModal((prev) => ({
            ...prev,
            open: true,
            msg: "somethingWentWrong",
          }));
          return;
        }
        if (searchString) {
          setInfoModal((prev) => ({
            ...prev,
            open: true,
            msg: "searchErr",
          }));
          return;
        }
        setInfoModal((prev) => ({
          ...prev,
          open: true,
          msg: "somethingWentWrong",
        }));
      }
    },
    [columList, searchString, siteData?.id]
  );

  useEffect(() => {
    if (siteData?.id) {
      getSchedulesList(currentPage).then(() => {
        sortingAction.current = false;
      });
    }
  }, [currentPage, columList, searchString, getSchedulesList, siteData?.id]);

  useEffect(() => {
    setCurrentPage(1); // this page change will trigger the getSchedulesList call.
  }, [siteData?.id, getSchedulesList]);

  const handlePageChange = useCallback((item) => {
    setCurrentPage(item + 1);
  }, []);

  const onAddSchdClose = useCallback(
    (val = "schdCreated", schdName = "") => {
      setIsAddScheduleOpen(false);
      if (val) {
        setSchdActionType("add");
        setInfoModal((prev) => ({
          ...prev,
          open: true,
          translated: val === "schdSavedAs", // if it's schdSavedAs then pass translated string to also pass schdName in trnslation.
          msg:
            val === "schdSavedAs"
              ? t("schdSavedAs", { schdName: schdName })
              : val,
        }));
        setTimeout(() => {
          getSchedulesList(currentPage);
        }, [2000]);
      }
    },
    [currentPage, getSchedulesList, t]
  );

  const onInfoModalClose = useCallback(() => {
    setInfoModal((prev) => ({ ...prev, open: false, msg: "" }));
  }, []);

  const onConfirmModalClose = useCallback(() => {
    //Reset on modal close
    setConfirmModal((prev) => ({
      ...prev,
      open: false,
      msg: "",
      type: "",
    }));
  }, []);

  const onConfirm = useCallback(
    async (type) => {
      if (type === "deleteSchd") {
        setIsLoading(true);
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
          msg: "",
          type: "",
        }));
        try {
          const scheduleId = currentSelectedRow?.PK.slice(4); // As pk id as SCH# as prefix.
          await deleteScheduleTemplate({ scheduleTemplateId: scheduleId });
          setInfoModal((prev) => ({
            ...prev,
            open: true,
            msg: "deleteSchdSuccessMsg",
          }));
          /* If user try to delete last element of that page then decrement currentPage by 1.
           * And if it's already last page then pass it as 1.
           */
          const page =
            currentPageListCount !== 1 ? currentPage : currentPage - 1 || 1;
          setTimeout(() => {
            if (page === currentPage) {
              getSchedulesList(page);
            } else {
              setCurrentPage(page); // this page change will trigger the getSchedulesList call.
            }
          }, 1000);
        } catch (err) {
          setInfoModal((prev) => ({
            ...prev,
            open: true,
            msg: "deleteSchdErrMsg",
          }));
          setIsLoading(false);
        }
      } else if (type === "fleetScheduleAction") {
        setCurrentTemplateData(confirmModal.data);
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
          msg: "",
          type: "",
          data: {},
        }));
      }
    },
    [
      currentSelectedRow?.PK,
      currentPageListCount,
      currentPage,
      getSchedulesList,
      confirmModal.data,
    ]
  );

  const applySorting = useCallback(
    (key) => {
      let listCopy = columList.map((item) => ({
        ...item,
        val: item.key === key ? item.val : "",
        clickCount: item.key === key ? item.clickCount : 0,
      }));

      let sortColumnIndex = listCopy.findIndex((item) => item.key === key);

      // Toggle sorting between ascending and descending
      if (
        listCopy[sortColumnIndex].clickCount === 0 ||
        listCopy[sortColumnIndex].clickCount === 2
      ) {
        listCopy[sortColumnIndex].clickCount = 1;
        listCopy[sortColumnIndex].val = "asc";
      } else if (listCopy[sortColumnIndex].clickCount === 1) {
        listCopy[sortColumnIndex].clickCount = 2;
        listCopy[sortColumnIndex].val = "desc";
      } else {
        listCopy[sortColumnIndex].clickCount = 1;
        listCopy[sortColumnIndex].val = "asc";
      }

      // Set sorting action flag and reset current page to 1
      sortingAction.current = true;
      setCurrentPage(1);

      // Update state
      setColumList(listCopy);
    },
    [columList]
  );

  const onRowActionclick = useCallback(
    (openStatus, index) => {
      openStatus
        ? setCurrentSelectedRow(scheduleList[index])
        : setCurrentSelectedRow();
    },
    [scheduleList]
  );

  const scheduleDescriptionEllipsis = (text, maxLength) => {
    if (!text) return "-";
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  useEffect(() => {
    setCurrentTemplateData(null);
    let currFleetData = getCurrentSiteLocalData(siteData.id);
    setIsSchdFleetOpen(currFleetData?.isOpenFleetPanel);
  }, [siteData.id]);

  const fleetSchdAction = useCallback(
    (scheduleDetail) => {
      let { action, fleetId, status } = getCurrentSiteLocalData(siteData.id);

      let isNotInitiated = status === fleetStatus.notInitiate;
      let isFleetInProgress = status === fleetStatus.InProgress;
      let isFleetComplete = status === fleetStatus.Completed;

      if (action) {
        if (fleetId && isFleetInProgress) {
          setInfoModal((prev) => ({
            ...prev,
            open: true,
            msg: t("fleetSchedule.exeInProcess"),
          }));
        } else if (fleetId && isFleetComplete) {
          updateLocalStorageData({ ...initStates }, siteData.id); // Remove from localstorage
          setCurrentTemplateData(scheduleDetail);
        } else if (isNotInitiated) {
          updateLocalStorageData({ schdState: schdState.enable }, siteData.id);
          setConfirmModal((prev) => ({
            ...prev,
            open: true,
            msg: t("fleetSchedule.discardMessage"),
            type: "fleetScheduleAction",
            data: { ...scheduleDetail },
          }));
        }
      } else {
        updateLocalStorageData({ schdState: schdState.enable }, siteData.id);
        setCurrentTemplateData(scheduleDetail);
      }
    },
    [siteData.id, t]
  );

  return (
    <div
      className={`schedule-dashboard-with-fleet`}
    >
      {/* Fleet schedule section Start */}
        <RightSidebar
          heading="Fleet Schedule"
          width="450px"
          collapsedWidth="40px"
          collapseStatus={(val) => {
            setIsSchdFleetOpen(!val);
            setCurrentTemplateData(null);
            updateLocalStorageData({ isOpenFleetPanel: !val }, siteData.id);
          }}
          isOpen={isSchdFleetOpen}
        >
          <FleetSchedule
            setLoading={(state) => setIsFleetLoading(state)}
            clickedTemplateData={currentTemplateData}
            resetCB={() => {
              setCurrentTemplateData(null);
            }}
          />
        </RightSidebar>
     
      {/* Fleet schedule section End */}

      {/* Schedule dashboard section Start */}
      <div className="bodyInner schedules-dashboard">
        <div className="bodyContainer">
          {/* Header Section Starts */}
          <div className={"header-container fixSubHeader"}>
            <div className="page-heading">
              <div className="header-title" id="schedule-count-title">
                {totalSchdCount >= 0
                  ? `${t("scheduleTemplates")} - ${
                      searchString
                        ? filteredSchdCount + "" + totalSchdCount
                        : totalSchdCount
                    }`
                  : ""}
              </div>
            </div>
            <div className="right-container">
              <div className="search-container">
                <input
                  id="search_input"
                  type="search"
                  placeholder={t("searchSchdPlaceholder")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
                {searchTerm?.length ? (
                  <span
                    className="close-icon"
                    id="close-icon"
                    onClick={resetSearch}
                    title={t("clear")}
                  >
                    {Icons.xCircle}
                  </span>
                ) : (
                  <span id="searchMagnifyIcon" className={`magnify-icon `}>
                    {Icons.magnify}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="roundBtn primary"
                id="add_schedules_btn"
                onClick={openAddSchedule}
              >
                {Icons.addIcon}
                <span id="add_schedules_text">{t("addTemplate")}</span>
              </button>
            </div>
          </div>
          {/* Header Section ends */}
          {/* Body Section Starts */}
          {scheduleList?.length ? (
            <div className="tableWrapper">
              <table
                className="table table-striped theme-table"
                // className={classNames("table table-striped", {
                //   "mb-0": 0,
                //   // "mb-0": downloadLists.length === 0,
                // })}
                id="schedule-table"
              >
                <thead>
                  <tr>
                    {columList.map((item, index) => (
                      <th
                        key={item.label}
                        id= {item.key}
                        name= {item.key}
                        className={`${item.key} schedule_header${index + 1} ${
                          index === 1 ? "description-column" : ""
                        }`}
                      >
                        <div className="sortIconWrapper">
                          <span title={item.label}>{item.label}</span>
                          {item.sort && (
                            <div
                              className="sortIcons"
                              title={sortLabel[item.val]}
                              onClick={() => applySorting(item.key)}
                            >
                              <button
                                type="button"
                                className={`ascbutton ${classNames({
                                  active: item.val === "asc",
                                })}`}
                                id="sort_asc"
                              >
                                {Icons.upArrowIcon}
                              </button>
                              <button
                                type="button"
                                className={classNames({
                                  active: item.val === "desc",
                                })}
                                id="sort_desc"
                              >
                                {Icons.downArrowIcon}
                              </button>
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scheduleList.map((scheduleDetail, index) => {
                    return (
                      <tr key={`${index + 1}`}>
                        <td id="schedule-name">{scheduleDetail?.scheduleTemplateName}</td>
                        <td id="schedule-desc" title={scheduleDetail?.scheduleTemplateDescription}>
                          {scheduleDescriptionEllipsis(
                            scheduleDetail?.scheduleTemplateDescription,
                            50
                          )}
                        </td>
                        <td id="schedule-updated-on">
                          {formatDate(scheduleDetail?.updatedDateTime, false)}
                        </td>
                        <td id="schedule-action">
                          {" "}
                          <Dropdown
                            title="Profile"
                            menu={{
                              items: actionItems,
                              className: `sch-action-menu ${
                                appTheme === "light"
                                  ? "light-theme"
                                  : "dark-theme"
                              }`,
                              onClick: ({ key }) => {
                                if (key === "fleet") {
                                  fleetSchdAction(scheduleDetail);
                                }
                              },
                            }}
                            trigger={["click"]}
                            // className="pointer"
                            overlayClassName="schd-action-menu"
                            onOpenChange={(openState) => {
                              onRowActionclick(openState, index);
                            }}
                          >
                            <span
                              className="action-icon"
                              id={`actionIcon_${index + 1}`}
                            >
                              {Icons.threeDots}
                            </span>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <></>
          )}
          {scheduleList && scheduleList.length === 0 && !isLoading ? (
            <div className="empty-screen-container">
              <EmptyScreen
                img={Icons.scheduleIcon}
                title={emptyScreenMsg.title}
                desc={emptyScreenMsg.subTitle}
              />
            </div>
          ) : (
            <div></div>
          )}

          {/* Body Section ends */}
          {(isLoading || isFleetLoading) && <LoadingSpinner />}
          {/* Pagination Section Starts */}
          <div className="bodyFooter footerFixed">
            {scheduleList?.length ? (
              <>
                <Pagination
                  currentPage={currentPage - 1}
                  totalPages={pageTotalCount}
                  pageCallBack={handlePageChange}
                />
              </>
            ) : (
              <></>
            )}
          </div>
          {/* Pagination Section Ends */}
        </div>

        {/* Schedule dashboard section End */}

        {/* Add Schedule Modal */}
        {isAddScheduleOpen && (
          <AddSchedules
            onClose={onAddSchdClose}
            selectedSchdData={currentSelectedRow}
            schdActionType={schdActionType}
          />
        )}

        {/* Schedule Info Modal */}
        {isSchdInfoModalOpen && (
          <ScheduleDetails
            onClose={() => {
              setIsSchdInfoModalOpen(false);
            }}
            scheduleDetails={currentSelectedRow}
          />
        )}

        {/* Err/Success Modal */}
        <ModalComponent
          idName="schdule-modal"
          isOpen={infoModal.open}
          onCloseModal={onInfoModalClose}
          descriptionText={
            infoModal?.translated ? infoModal.msg : t(infoModal.msg)
          }
        />

        {/* Comfirmation Modal */}
        <ModalComponent
          idName="schedule-confirm-modal"
          isOpen={confirmModal.open}
          onCloseModal={onConfirmModalClose}
          descriptionText={confirmModal.msg}
          needConfirmation={true}
          onConfirm={() => {
            onConfirm(confirmModal.type);
          }}
        />
      </div>
    </div>
  );
};

export default SchedulesDashboard;
